<template>
  <div>
    <template v-if="activelyTransacting() && !limitOrder">
      <v-btn
        :style="buttonStyle()"
        :color="getButtonColor()"
        :class="getTextClass() + ' ma-0'"
        @click="addToTradeTicket()"
        tile
      >{{ buySell }} {{ computeTotal() | toCurrency }}
        <volume-discount-hint
          :item="item"
          :buy-sell="buySell"
          class="ml-1"
        ></volume-discount-hint
        >
      </v-btn>
      <div
        class="text-center text-caption text--lighten-4"
        :style="
          'line-height: 1rem; margin: 0px auto 0px auto;width: ' +
          buttonWidth() +
          ';'
        "
      >
        <span v-if="showFormulas && getSelectedTradingRole !== TradingRoles.SALES_REP">{{ getPremiumText(item, buySell) }}</span>
      </div>
      <div
        class="text-center text-caption text--lighten-4"
        :style="'width: ' + buttonWidth() + ';margin:0px auto;'"
        v-if="itemDescription() && !hideStockDescription"
      >
        {{ itemDescription() }}
      </div>
    </template>
    <template
      v-else-if="
        !activelyTransacting() && potentiallyTransacting() && !limitOrder
      "
    >
      <template v-if="showAlertMe()">
        <v-btn
          v-if="
            lodash
              .get(item, `productStockAlert`, [])
              .filter((x) => x.transactionType === buySell).length === 0
          "
          elevation="0"
          :style="buttonStyle() + ';color:#666'"
          @click="alertMe()"
          :loading="loadingStockAlert[buySell]"
          tile
        >
          <v-icon>mdi-bell-plus</v-icon>
          Alert Me
        </v-btn
        >
        <v-btn
          v-else
          elevation="0"
          :style="buttonStyle() + ';color:#666'"
          @click="unAlertMe()"
          :loading="loadingStockAlert[buySell]"
          tile
        >
          <v-icon>mdi-bell-remove</v-icon>
          Cancel Alert
        </v-btn
        >
      </template>
      <v-btn
        v-else
        elevation="0"
        href="tel:+13156371300"
        text
        :style="'width:' + buttonWidth() + ';height:' + buttonHeight() + ';'"
        tile
      >Call
      </v-btn
      >
    </template>
    <template
      v-else-if="
        (activelyTransacting() || potentiallyTransacting()) && limitOrder
      "
    >
      <v-btn
        elevation="0"
        :style="buttonStyle()"
        @click="openLimitOrder()"
        :disabled="!isEligibleForLimitOrder()"
        :color="getButtonColor()"
        :class="getTextClass()"
        tile
      >
        <v-icon>mdi-cash-clock</v-icon>
        Limit Order
      </v-btn
      >
      <span class="text-caption" v-if="!isEligibleForLimitOrder()"
      >Not eligible for limit orders</span
      >

      <div
        class="text-center text-caption text--lighten-4"
        :style="'width: ' + buttonWidth() + ';margin:0px auto;'"
        v-if="showFormulas && getSelectedTradingRole !== TradingRoles.SALES_REP"
      >
        &nbsp;
      </div>
    </template>
    <div v-else :style="'width:' + buttonWidth() + ';'"></div>
  </div>
</template>

<script>
import productMixin from '@/mixins/product'
import guardsMixin from '@/mixins/guards'
import {mapGetters, mapState} from 'vuex'
import _ from 'lodash'
import VolumeDiscountHint from '@/components/catalog/VolumeDiscountHint'
import {TradingRoles} from '@/util/constants'

const PRODUCT_UPDATES_SUBSCRIPTION_ID = `5`

export default {
  props: [
    'item',
    'buySell',
    'showFormulas',
    'hideStockDescription',
    'color',
    'limitOrder',
    'quantity',
    'width',
    'height',
  ],
  mixins: [productMixin, guardsMixin],
  components: {VolumeDiscountHint},
  computed: {
    ...mapGetters([
      'getSelectedCompany',
      'getExistingItemInCart',
      'getSelectedTradingRole',
      'getUserProfile',
      'getUserSubscriptions',
    ]),
    ...mapState({
      spotPrices: (state) => state.user.spotPrices,
    }),
  },
  data() {
    return {
      alertSellSide: false,
      lodash: _,
      TradingRoles,
      loadingStockAlert: {
        sell: false,
        buy: false,
      },
    }
  },
  methods: {
    getButtonColor() {
      if (this.color) return this.color
      if (this.limitOrder) {
        return 'primary' // Limit orders are primary
      }
      if (this.buySell === `sell`) {
        return 'accent' // Sell buttons are accent
      }
      return 'secondary' // Buy buttons are secondary
    },
    getTextClass() {
      if (this.limitOrder) return 'text--white'
      return this.buySell === `sell` ? 'lighten-1' : 'text--primary'
    },
    activelyTransacting() {
      if (this.buySell === `sell`) return this.item.activelyBuying
      return this.item.activelySelling
    },
    potentiallyTransacting() {
      if (this.buySell === `sell`)
        return _.get(this.item, `buySell`) !== `Sell Only`
      return _.get(this.item, `buySell`) !== `Buy Only`
    },
    itemDescription() {
      return this.buySell === `sell`
        ? this.item.saleNote
        : this.item.description
    },
    showAlertMe() {
      // For now only show alerts on the buy side
      return this.buySell !== `sell`
    },
    buttonWidth() {
      return this.width ? this.width : '160px'
    },
    buttonHeight() {
      return this.height ? this.height : '24px'
    },
    buttonStyle() {
      return `height:${this.buttonHeight()}; width:${this.buttonWidth()};`
    },
    async alertMe() {
      this.loadingStockAlert[this.buySell] = true
      try {
        let text = `We will notify you when this product is back in stock.`
        await Promise.all([
          this.$store.dispatch('createProductStockAlert', {
            item: this.item,
            buySell: this.buySell,
          }),
          this.$store.dispatch(`fetchUserSubscriptions`),
        ])
        if (
          this.getUserSubscriptions.indexOf(PRODUCT_UPDATES_SUBSCRIPTION_ID) ===
          -1 ||
          !_.get(
            this.getUserProfile,
            `communicationPreferencesConfirmed`,
            false
          )
        ) {
          text += ` Please take a moment to review your communication preferences. Product Updates must be selected to receive stock notifications.`
          window._VMA.$emit('OPEN_COMMUNICATION_PREFERENCES', {text})
        } else {
          // The Stock Alert Added dialog prompts user to create limit order.
          // @todo Expose this to all users in final release
          if (
            this.buySell === `buy` &&
            this.limitOrdersEnabled() &&
            this.isEligibleForLimitOrder()
          )
            window._VMA.$emit('SHOW_STOCK_ALERT_ADDED', {
              text,
              selectedProduct: this.item,
              buySell: this.buySell,
            })
          // Sell limit orders are not currently supported
          else
            window._VMA.$emit('SHOW_SNACKBAR', {
              text,
              color: 'success',
            })
        }
      } catch (e) {
        window._VMA.$emit('SHOW_SNACKBAR', {
          text: e.message,
          color: 'error',
        })
      }
      this.loadingStockAlert[this.buySell] = false
    },
    openLimitOrder() {
      this.$emit('added')
      window._VMA.$emit('SHOW_LIMIT_ORDER', {
        selectedProduct: this.item,
        buySell: this.buySell,
        quantity: this.quantity || this.getDefaultQty(this.item, this.buySell),
      })
    },
    isEligibleForLimitOrder() {
      if (this.buySell === `buy`)
        return this.item.askPriceFormat !== `Fixed` && this.item.askPremium
      return this.item.bidPriceFormat !== `Fixed` && this.item.bidPremium
    },
    async unAlertMe() {
      this.loadingStockAlert[this.buySell] = true
      try {
        await this.$store.dispatch('cancelProductStockAlert', {
          item: this.item,
          buySell: this.buySell,
        })
        window._VMA.$emit('SHOW_SNACKBAR', {
          text: `Alert has been canceled.`,
          color: 'success',
        })
      } catch (e) {
        window._VMA.$emit('SHOW_SNACKBAR', {
          text: e.message,
          color: 'error',
        })
      }
      this.loadingStockAlert[this.buySell] = false
    },
    addToTradeTicket() {
      try {
        const existing = this.getExistingItemInCart(this.item, this.buySell)
        window._VMA.$emit('SHOW_ADD_TO_TRADE_TICKET', {
          selectedProduct: this.item,
          buySell: this.buySell,
          quantity: existing
            ? this.getNextIncrementQty(this.item, existing.quantity, this.buySell)
            : this.getDefaultQty(this.item, this.buySell),
        })
        this.$emit('added')
      } catch (e) {
        window._VMA.$emit('SHOW_SNACKBAR', {
          text: e.message,
          color: 'error',
        })
      }
    },
    computeTotal() {
      if (this.buySell === `sell`) return this.computeBidTotal()
      return this.computeAskTotal()
    },
    computeAskTotal() {
      return this.computePrices(
        this.item,
        this.spotPrices,
        this.getSelectedCompany,
        this.getSelectedTradingRole
      ).ask
    },
    computeBidTotal() {
      return this.computePrices(
        this.item,
        this.spotPrices,
        this.getSelectedCompany,
        this.getSelectedTradingRole
      ).bid
    },
  },
}
</script>
